import {
  cn,
  formatCurrency,
  formatNumber,
  formatDate as formatUiDate,
  useTranslation,
} from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  CalendarDays,
  CarFront,
  ClipboardList,
  Clock3,
  FileText,
  Gauge,
  ReceiptText,
  UserPlus,
  Wrench,
} from 'lucide-react';

import { type AccessRule, useAccess } from '../../auth/useAccess.js';

import { EstimatePipeline } from './components/EstimatePipeline.js';
import { FleetAlerts } from './components/FleetAlerts.js';
import { HomeGreeting } from './components/HomeGreeting.js';
import { InvoiceStatusRing } from './components/InvoiceStatusRing.js';
import { InvoicesDueQueue } from './components/InvoicesDueQueue.js';
import { KpiTiles } from './components/KpiTiles.js';
import { PayrollPeriodCard } from './components/PayrollPeriodCard.js';
import { RecentTime } from './components/RecentTime.js';
import { RevenueTrendCard } from './components/RevenueTrendCard.js';
import { SetupNudge } from './components/SetupNudge.js';
import type { OverviewResponse } from './overviewTypes.js';

/**
 * The Home KPI dashboard (prod routes/dashboards.tsx `/dashboard` rebuilt as the
 * SPA index — Plan 22-28). Composes the section cards from the single coarse
 * `/api/v1/overview` envelope: the setup nudge (only when the company profile is
 * incomplete), the MTD KPI tiles, the estimate pipeline, the invoices-due queue,
 * recent time, and fleet alerts. Money/date formatting is locale-aware (I18N-05);
 * every color is a locked token (BRAND-06 / lint:no-hex).
 */

/**
 * The create row, and the rule the api worker applies to each destination.
 *
 * ⚠ THE RULE IS THE ONE THAT GUARDS THE ACTION, not the one that guards the section it lives in.
 * Reading jobs is `jobs.view`; CREATING one is `jobs.create` on `POST /api/v1/jobs`, and the top
 * bar's own New Job button gates on that key for exactly that reason. Every rule below was read off
 * the route it fires, not inferred from the label:
 *   POST /jobs        jobs.create
 *   POST /invoices    invoices.create        (api.ts:5129)
 *   POST /estimates   requireManagerOrAdmin  (api.ts:10130)
 *   POST /employees   employees.create       (api.ts:6040)
 *   GET  /timesheets  time.view              (api.ts:2853)
 *   GET  /dispatch    jobs.view              (api.ts:12637)
 *
 * BOTH RULES, NOT THE STRICTER ONE. An action needs the rule that lets somebody OPEN the
 * destination AND the rule that lets the action succeed, and neither dominates the other:
 * `/jobs/new` is reachable on `jobs.view` while `POST /jobs` wants `jobs.create`, and `/employees`
 * is reachable on `employees.view` while `POST /employees` wants `employees.create`. `AccessRule` has no AND, so the entry
 * carries a list and every rule in it has to hold. `HomeCard.test.tsx` asserts each list contains
 * what `resolveRouteAccess` returns for the destination, so the row cannot out-live the router.
 *
 * HIDING IS UX, NEVER AUTHORIZATION. Each of those checks runs again on the server, whatever this
 * screen chose to draw.
 */
export const QUICK_ACTIONS: ReadonlyArray<{
  to: string;
  labelKey: string;
  Icon: LucideIcon;
  /** ALL of them must hold. `AccessRule` cannot express AND, and every create action needs it. */
  rules: AccessRule[];
}> = [
  {
    to: '/jobs/new',
    labelKey: 'home:quickActions.newJob',
    Icon: Wrench,
    rules: [{ permission: 'jobs.view' }, { permission: 'jobs.create' }],
  },
  {
    to: '/invoices/new',
    labelKey: 'home:quickActions.createInvoice',
    Icon: ReceiptText,
    rules: [{ permission: 'invoices.view' }, { permission: 'invoices.create' }],
  },
  {
    to: '/estimates/new',
    labelKey: 'home:quickActions.newEstimate',
    Icon: FileText,
    rules: [{ minRole: 'Manager' }],
  },
  {
    to: '/timesheets',
    labelKey: 'home:quickActions.logTime',
    Icon: Clock3,
    rules: [{ permission: 'time.view' }],
  },
  {
    to: '/dispatch',
    labelKey: 'home:quickActions.schedule',
    Icon: CalendarDays,
    rules: [{ permission: 'jobs.view' }],
  },
  {
    to: '/employees/new',
    labelKey: 'home:quickActions.addEmployee',
    Icon: UserPlus,
    rules: [{ permission: 'employees.view' }, { permission: 'employees.create' }],
  },
];

/**
 * The shortcut row, and the rule guarding each section.
 *
 * These are the SIDEBAR's destinations, so they carry the sidebar's rules — `resolveRouteAccess`
 * resolves the same value for every path here, and `HomeView.test.tsx` asserts that pair rather
 * than trusting it. A shortcut row that offered a section the sidebar hides was the defect: a crew
 * member granted `reports.view` landed on this screen and was handed Estimates, Invoices and Fleet.
 */
export const CATEGORY_SHORTCUTS: ReadonlyArray<{
  to: string;
  labelKey: string;
  Icon: LucideIcon;
  className: string;
  access: AccessRule;
}> = [
  {
    to: '/jobs',
    labelKey: 'home:categories.jobs',
    Icon: Wrench,
    className: 'bg-category-jobs/12 text-category-jobs ring-category-jobs/20',
    access: { permission: 'jobs.view' },
  },
  {
    to: '/estimates',
    labelKey: 'home:categories.estimates',
    Icon: FileText,
    className: 'bg-category-estimates/12 text-category-estimates ring-category-estimates/20',
    access: { minRole: 'Manager' },
  },
  {
    to: '/invoices',
    labelKey: 'home:categories.invoices',
    Icon: ReceiptText,
    className: 'bg-category-invoices/12 text-category-invoices ring-category-invoices/20',
    access: { permission: 'invoices.view' },
  },
  {
    to: '/dispatch',
    labelKey: 'home:categories.dispatch',
    Icon: CalendarDays,
    className: 'bg-category-dispatch/12 text-category-dispatch ring-category-dispatch/20',
    access: { permission: 'jobs.view' },
  },
  {
    to: '/timesheets',
    labelKey: 'home:categories.timesheets',
    Icon: ClipboardList,
    className: 'bg-category-timesheets/12 text-category-timesheets ring-category-timesheets/20',
    access: { permission: 'time.view' },
  },
  {
    to: '/fleet',
    labelKey: 'home:categories.fleet',
    Icon: CarFront,
    className: 'bg-category-fleet/12 text-category-fleet ring-category-fleet/20',
    access: { permission: 'fleet.view' },
  },
  {
    to: '/reports',
    labelKey: 'home:categories.reports',
    Icon: BarChart3,
    className: 'bg-category-reports/12 text-category-reports ring-category-reports/20',
    access: { permission: 'reports.view' },
  },
];

/** Literal class names: Tailwind scans source text, so a `xl:grid-cols-${n}` template emits nothing. */
const GLANCE_COLUMNS: Record<number, string> = {
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
};
const TEAM_COLUMNS: Record<number, string> = {
  1: '',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
};

function SectionHeading({ eyebrow, id, title }: { eyebrow: string; id: string; title: string }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {eyebrow}
        </p>
        <h2 id={id} className="font-display text-xl font-semibold tracking-tight text-foreground">
          {title}
        </h2>
      </div>
    </div>
  );
}

function InsightCallout({
  stats,
  money,
  locale,
}: {
  stats: OverviewResponse['stats'];
  money: (value: number) => string;
  locale: string;
}) {
  const { t } = useTranslation();
  const canAccess = useAccess();
  // An overdue balance is the same fact whoever is reading it, so the MESSAGE is never gated —
  // ANDing the role check into this is what made a crew member with three overdue invoices read
  // "Revenue is moving up". Only the way OUT depends on the viewer: `GET /api/v1/invoices` is
  // `requireManagerOrAdmin`, while `/reports` is what selected this layout and is always openable.
  const hasOverdue = stats.overdueInvoiceCount > 0;
  const canOpenInvoices = canAccess({ minRole: 'Manager' });
  const revenueDelta =
    stats.prevRevenueMtd && stats.prevRevenueMtd > 0
      ? (stats.revenueMtd - stats.prevRevenueMtd) / stats.prevRevenueMtd
      : 0;
  const delta = formatNumber(revenueDelta, locale, {
    style: 'percent',
    signDisplay: 'always',
    maximumFractionDigits: 1,
  });
  // The title and the body were hardcoded to the rise, so a month that fell 12% was announced as
  // "Revenue is moving up" with a minus sign beside it.
  const revenueKey = revenueDelta < 0 ? 'revenueDown' : 'revenue';

  return (
    <aside className="rounded-2xl border border-info/25 bg-info/8 p-4 shadow-elev-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-info/12 text-info ring-1 ring-info/25">
            <Gauge className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-2xs font-semibold uppercase tracking-[0.16em] text-info">
              {t('home:insight.eyebrow')}
            </p>
            <h2 className="mt-1 font-display text-lg font-semibold text-foreground">
              {t(hasOverdue ? 'home:insight.overdueTitle' : `home:insight.${revenueKey}Title`)}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(hasOverdue ? 'home:insight.overdueBody' : `home:insight.${revenueKey}Body`, {
                total: money(stats.overdueInvoiceTotal),
                count: stats.overdueInvoiceCount,
                delta,
              })}
            </p>
          </div>
        </div>
        <Link
          to={hasOverdue && canOpenInvoices ? '/invoices' : '/reports'}
          className="inline-flex h-9 shrink-0 items-center justify-center rounded-full border border-info/25 bg-card px-3 text-sm font-semibold text-info shadow-elev-1 transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-info/10 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
          {t(
            hasOverdue && canOpenInvoices
              ? 'home:insight.reviewInvoices'
              : 'home:insight.openReports',
          )}
        </Link>
      </div>
    </aside>
  );
}

export function HomeView({ overview }: { overview: OverviewResponse }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const canAccess = useAccess();

  // Filtered before the row is drawn, not hidden inside it: a row whose every destination is
  // refused must not render its heading and its gap either.
  const categories = CATEGORY_SHORTCUTS.filter((entry) => canAccess(entry.access));
  const quickActions = QUICK_ACTIONS.filter((entry) => entry.rules.every(canAccess));

  /*
    A CARD IS A DESTINATION TOO, and gating only the two link rows left the bigger hole open: the
    invoice ring and the receivables queue render live `/invoices` links, and `GET /api/v1/invoices`
    is `requireManagerOrAdmin`. A crew member holding `reports.view` was handed rows that open a
    screen the server refuses — and, worse than a dead link, the estimate pipeline's value and every
    other employee's labor cost were on the page before they clicked anything.

    Each rule below is the one `resolveRouteAccess` gives that card's own destination, so a card and
    the shortcut to the same section appear and disappear together. `RecentTime` is the exception
    and it is a role: it lists OTHER employees by name with their labor cost, and the rule the api
    worker applies to that is the role, not a key — `GET /timesheets` force-scopes an Employee-role
    caller to their own row (api.ts:2870-2871) whatever keys they hold.

    `GET /api/v1/payroll` is `requireApiAdmin`. Mounting the card is what fires the request, so that
    gate has to sit here rather than inside it.
  */
  const showPipeline = canAccess({ minRole: 'Manager' });
  // Two gates, both must hold. `/invoices` opens on the key, but `GET /api/v1/invoices` is
  // `requireManagerOrAdmin`: an Employee-role account granted `invoices.view` as an override still
  // gets a 403 from the list, so the key alone would mount two cards of balances and dead links.
  const showInvoices =
    canAccess({ minRole: 'Manager' }) && canAccess({ permission: 'invoices.view' });
  const showFleet = canAccess({ permission: 'fleet.view' });
  const showRecentTime = canAccess({ minRole: 'Manager' });
  const showPayroll = canAccess({ minRole: 'Admin' });

  // The revenue chart spans two columns, so the count is a span count, not a card count: drop both
  // cards beside it and the row becomes two columns wide with the chart filling them, rather than
  // a half-width chart with a void where the money used to be.
  const glanceSpan = 2 + (showPipeline ? 1 : 0) + (showInvoices ? 1 : 0);
  const teamCards = [showRecentTime, showPayroll, showFleet].filter(Boolean).length;

  const money = (value: number): string =>
    formatCurrency(value || 0, locale, 'USD', { maximumFractionDigits: 0 });
  const formatDate = (date: string): string => {
    try {
      return formatUiDate(date, locale, { month: 'short', day: 'numeric' });
    } catch {
      return date;
    }
  };

  return (
    <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
      <HomeGreeting />

      {categories.length > 0 ? (
        <nav
          aria-label={t('home:categories.heading')}
          className="mb-3 overflow-x-auto pb-1 [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] pe-8"
        >
          <div className="flex min-w-max gap-2 lg:min-w-0 lg:grid lg:grid-cols-7">
            {categories.map(({ to, labelKey, Icon, className }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-2 rounded-2xl border border-border bg-card p-2.5 pr-3 text-sm font-semibold text-foreground shadow-elev-1 transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-secondary active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <span
                  className={cn('grid size-9 place-items-center rounded-full ring-1', className)}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <span className="whitespace-nowrap">{t(labelKey)}</span>
              </Link>
            ))}
          </div>
        </nav>
      ) : null}

      {quickActions.length > 0 ? (
        <nav aria-label={t('home:quickActions.heading')} className="mb-6 flex flex-wrap gap-2">
          {quickActions.map(({ to, labelKey, Icon }) => (
            <Link
              key={to}
              to={to}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card px-3 text-sm font-semibold text-foreground shadow-elev-1 transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-secondary active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
              {t(labelKey)}
            </Link>
          ))}
        </nav>
      ) : null}

      <div className="grid gap-6">
        <InsightCallout stats={overview.stats} money={money} locale={locale} />

        <section className="grid gap-3" aria-labelledby="home-kpi-band">
          <SectionHeading
            id="home-kpi-band"
            eyebrow={t('home:sections.kpiEyebrow')}
            title={t('home:sections.kpiTitle')}
          />
          <KpiTiles stats={overview.stats} money={money} />
        </section>

        {overview.companyConfigured ? null : <SetupNudge />}

        <section className="grid gap-3" aria-labelledby="home-glance-band">
          <SectionHeading
            id="home-glance-band"
            eyebrow={t('home:sections.glanceEyebrow')}
            title={t('home:sections.glanceTitle')}
          />
          <div className={cn('grid gap-5', GLANCE_COLUMNS[glanceSpan])}>
            <RevenueTrendCard stats={overview.stats} money={money} />
            {showPipeline ? (
              <EstimatePipeline stats={overview.estimateStats} money={money} />
            ) : null}
            {showInvoices ? (
              <InvoiceStatusRing invoices={overview.invoicesDue} stats={overview.stats} />
            ) : null}
          </div>
        </section>

        {/* One card, so the heading goes with it rather than standing over an empty band. */}
        {showInvoices ? (
          <section className="grid gap-3" aria-labelledby="home-receivables-band">
            <SectionHeading
              id="home-receivables-band"
              eyebrow={t('home:sections.receivablesEyebrow')}
              title={t('home:sections.receivablesTitle')}
            />
            <InvoicesDueQueue
              invoices={overview.invoicesDue}
              stats={overview.stats}
              money={money}
              formatDate={formatDate}
            />
          </section>
        ) : null}

        {teamCards > 0 ? (
          <section className="grid gap-3" aria-labelledby="home-team-band">
            <SectionHeading
              id="home-team-band"
              eyebrow={t('home:sections.teamEyebrow')}
              title={t('home:sections.teamTitle')}
            />
            {/*
              The column count follows how many cards a viewer actually holds, so a single card
              never sits in a third of the width with two empty thirds beside it. Payroll sits
              between the two because it is the same subject as the hours above it, aggregated.
            */}
            <div className={cn('grid gap-5', TEAM_COLUMNS[teamCards])}>
              {showRecentTime ? (
                <RecentTime entries={overview.recentTime} money={money} formatDate={formatDate} />
              ) : null}
              {showPayroll ? <PayrollPeriodCard /> : null}
              {showFleet ? <FleetAlerts alerts={overview.fleetAlerts} /> : null}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
